export const ACCEPTANCE_ITEM_IMPORT_COLUMNS = [
  { key: "importCode", header: "STT", aliases: ["Mã dự toán", "Mã cấu trúc"] },
  { key: "itemName", header: "Dự toán", aliases: ["Dự toán"] },
  { key: "difficultyLevel", header: "Mức KK", aliases: ["Mức KK", "Mức độ khó khăn"] },
  { key: "reportedQuantity", header: "Khối lượng thực hiện" },
  { key: "acceptedQuantity", header: "Khối lượng nghiệm thu" },
  { key: "status", header: "Trạng thái nghiệm thu", aliases: ["Trạng thái"] },
  { key: "rejectReason", header: "Lý do không nghiệm thu", aliases: ["Lý do chưa nghiệm thu"] },
  { key: "notes", header: "Ghi chú" },
];

export const ACCEPTANCE_STATUS_OPTIONS = [
  "Chưa nghiệm thu",
  "Không nghiệm thu",
  "Được nghiệm thu",
  "Thực hiện lại",
];

const HEADER_BY_KEY = Object.fromEntries(
  ACCEPTANCE_ITEM_IMPORT_COLUMNS.map((column) => [column.key, column.header]),
);

// Chuẩn hóa text để kiểm tra dữ liệu trống.
const normalizeText = (value) =>
  value === null || value === undefined ? "" : value.toString().trim();

// Chuẩn hóa text để tra cứu không phân biệt dấu, hoa thường và khoảng trắng.
const normalizeLookup = (value) =>
  normalizeText(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/[_\s]+/g, " ");

// Chuẩn hóa mã dự toán để so khớp STT trong file với item đã lưu.
export const normalizeImportCode = (value) =>
  normalizeText(value).replace(/\s+/g, "");

// Chuyển giá trị Excel sang số để kiểm tra khối lượng.
const parseNumberValue = (value) => {
  if (value === null || value === undefined || normalizeText(value) === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : Number.NaN;

  const rawValue = value.toString().trim();
  let normalized = rawValue;
  if (rawValue.includes(".") && rawValue.includes(",")) {
    normalized = rawValue.replace(/\./g, "").replace(",", ".");
  } else if (rawValue.includes(",")) {
    normalized = rawValue.replace(",", ".");
  } else if (/^\d{1,3}(\.\d{3})+(\.\d+)?$/.test(rawValue)) {
    normalized = rawValue.replace(/\./g, "");
  }

  const numberValue = Number(normalized);
  return Number.isFinite(numberValue) ? numberValue : Number.NaN;
};

// Ghi nhận lỗi kiểm tra dữ liệu import theo sheet, dòng và cột.
const addError = (errors, row, columnKey, value, message) => {
  errors.push({
    sheetName: row?.sheetName || "",
    rowNumber: row?.rowNumber || "",
    importCode: row?.sourceImportCode || row?.importCode || "",
    itemCode: row?.sourceImportCode || row?.importCode || "",
    column: columnKey ? HEADER_BY_KEY[columnKey] || columnKey : "",
    value: value === null || value === undefined ? "" : value,
    message,
  });
};

// Lấy id theo cả camelCase và PascalCase từ dữ liệu API.
const pickId = (value, key) =>
  value?.[key] ?? value?.[`${key[0].toUpperCase()}${key.slice(1)}`];

// Lấy tên scope theo dữ liệu bảng SubcontractItemScopes.
const getScopeName = (scope) =>
  scope?.scopeName ?? scope?.ScopeName ?? scope?.name ?? scope?.Name ?? "";

// Lấy scopeId theo các dạng field đang dùng ở frontend/backend.
const getScopeId = (value) =>
  value?.subcontractItemScopeId ?? value?.SubcontractItemScopeId ?? value?.scopeId ?? value?.ScopeId;

// Lấy itemId theo các dạng field đang dùng ở frontend/backend.
const getItemId = (item) => item?.itemId ?? item?.ItemId;

// Lấy mã dự toán theo các dạng field đang dùng ở frontend/backend.
const getItemCode = (item) => item?.itemCode ?? item?.ItemCode ?? "";

// Lấy tên dự toán theo các dạng field đang dùng ở frontend/backend.
const getItemName = (item) => item?.itemName ?? item?.ItemName ?? "";

const getDifficultyLevel = (item) => item?.difficultyLevel ?? item?.DifficultyLevel ?? null;

// Kiểm tra item có phải dự toán cha hay không để không nghiệm thu trực tiếp.
const itemHasChildren = (item) =>
  Boolean(item?.hasChildren ?? item?.HasChildren ?? (Array.isArray(item?.children) && item.children.length > 0));

// Làm phẳng cây dự toán nếu dữ liệu items có children.
const flattenItems = (items = []) =>
  items.flatMap((item) => [item, ...flattenItems(item.children || item.Children || [])]);

// Tạo danh sách scope hợp lệ, bỏ scope tổng hợp vì không dùng để nghiệm thu.
const flattenScopes = (sections = []) =>
  sections.flatMap((section) =>
    (section.scopes || section.Scopes || []).map((scope) => ({
      ...scope,
      sectionId: pickId(section, "subcontractSectionId"),
      sectionName: section.sectionName || section.SectionName || section.name,
      sectionType: section.sectionType || section.SectionType,
    })),
  );

// Tạo map trạng thái nghiệm thu hợp lệ theo danh sách quy định.
const buildStatusMap = () =>
  new Map(ACCEPTANCE_STATUS_OPTIONS.map((status) => [normalizeLookup(status), status]));

const getProfileId = (profile) => profile?.acceptanceProfileId ?? profile?.AcceptanceProfileId;

const getProfileTime = (profile) => {
  const value = profile?.acceptanceDate ?? profile?.AcceptanceDate;
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

const isBeforeProfile = (profile, currentProfile) => {
  const profileId = Number(getProfileId(profile) || 0);
  const currentProfileId = Number(getProfileId(currentProfile) || 0);
  const profileTime = getProfileTime(profile);
  const currentTime = getProfileTime(currentProfile);

  if (currentTime !== null && profileTime !== null && profileTime !== currentTime) {
    return profileTime < currentTime;
  }
  if (currentTime === null && profileTime !== null) return true;
  if (currentTime !== null && profileTime === null) return false;
  return profileId < currentProfileId;
};

// Tính số lượng đã nghiệm thu trước kỳ hiện tại để xác định khối lượng còn lại.
const buildAcceptedBeforeMap = (acceptances = [], currentAcceptanceProfileId = null) => {
  const acceptedByItemId = new Map();
  const currentProfile = acceptances.find((profile) =>
    String(getProfileId(profile)) === String(currentAcceptanceProfileId),
  );

  acceptances.forEach((profile) => {
    const profileId = getProfileId(profile);
    if (
      currentAcceptanceProfileId &&
      String(profileId) === String(currentAcceptanceProfileId)
    ) {
      return;
    }
    if (currentProfile && !isBeforeProfile(profile, currentProfile)) return;

    (profile.items || profile.Items || []).forEach((item) => {
      const itemId = item.itemId ?? item.ItemId;
      if (!itemId) return;
      const acceptedQuantity = Number(item.acceptedQuantity ?? item.AcceptedQuantity ?? 0);
      acceptedByItemId.set(
        String(itemId),
        (acceptedByItemId.get(String(itemId)) || 0) + acceptedQuantity,
      );
    });
  });
  return acceptedByItemId;
};

// Tạo tập item đã có trong kỳ hiện tại để tránh import tạo trùng chi tiết.
const buildCurrentAcceptanceItemSet = (acceptances = [], currentAcceptanceProfileId = null) => {
  if (!currentAcceptanceProfileId) return new Set();

  const currentProfile = acceptances.find((profile) =>
    String(profile.acceptanceProfileId ?? profile.AcceptanceProfileId) === String(currentAcceptanceProfileId),
  );
  return new Set(
    (currentProfile?.items || currentProfile?.Items || [])
      .map((item) => item.itemId ?? item.ItemId)
      .filter(Boolean)
      .map(String),
  );
};

// So sánh số thập phân có dung sai nhỏ để tránh lỗi làm tròn Excel.
const isGreaterThan = (left, right) => Number(left) - Number(right) > 0.0000001;

// Tính các cột hệ thống sau khi dòng import đã qua toàn bộ kiểm tra.
const calculateAcceptanceValues = ({ unitPrice, reportedQuantity, acceptedQuantity, remainingBefore }) => {
  const rejectedQuantity = reportedQuantity - acceptedQuantity;
  const remainingQuantity = remainingBefore - acceptedQuantity;
  const acceptanceAmount = acceptedQuantity * unitPrice;

  return {
    rejectedQuantity: Math.round(rejectedQuantity * 1000000) / 1000000,
    remainingQuantity: Math.round(remainingQuantity * 1000000) / 1000000,
    acceptanceAmount: Math.round(acceptanceAmount * 100) / 100,
  };
};

// Tách input để hỗ trợ cả dạng rows array và object trả về từ readAcceptanceItemsImportExcel.
const unwrapImportInput = (input) => {
  if (Array.isArray(input)) {
    return {
      rows: input,
      sheets: [...new Set(input.map((row) => row.sheetName).filter(Boolean))],
      readErrors: [],
    };
  }

  return {
    rows: input?.rows || [],
    sheets: input?.sheets || [...new Set((input?.rows || []).map((row) => row.sheetName).filter(Boolean))],
    readErrors: input?.errors || [],
  };
};

// Kiểm tra dữ liệu import chi tiết nghiệm thu và trả về payload hợp lệ để import.
export const validateAcceptanceItemsImport = ({
  importData,
  rows,
  sheets,
  readErrors = [],
  sections = [],
  items = [],
  acceptances = [],
  acceptanceProfileId = null,
} = {}) => {
  const input = unwrapImportInput(importData || { rows, sheets, errors: readErrors });
  const errors = [];
  const statusMap = buildStatusMap();
  const validScopes = flattenScopes(sections).filter(
    (scope) => normalizeLookup(scope.scopeType || scope.ScopeType) !== "total",
  );
  const scopeByName = new Map(
    validScopes.map((scope) => [normalizeLookup(getScopeName(scope)), scope]),
  );
  const itemRows = flattenItems(items);
  const acceptedBeforeByItemId = buildAcceptedBeforeMap(acceptances, acceptanceProfileId);
  const currentAcceptanceItemIds = buildCurrentAcceptanceItemSet(acceptances, acceptanceProfileId);
  const fileItemKeys = new Set();
  const normalizedRows = [];

  input.readErrors.forEach((error) => {
    addError(errors, { sheetName: error.sheetName }, "", "", error.message);
  });

  input.sheets.forEach((sheetName) => {
    if (!scopeByName.has(normalizeLookup(sheetName))) {
      addError(
        errors,
        { sheetName },
        "",
        sheetName,
        `Sheet "${sheetName}" không tồn tại trong danh sách phân cấp giao khoán hợp lệ`,
      );
    }
  });

  if (!input.rows.length) {
    addError(errors, {}, "", "", "File import không có dòng chi tiết nghiệm thu");
  }

  input.rows.forEach((sourceRow) => {
    const sourceImportCode = normalizeImportCode(sourceRow.importCode);
    const sheetName = normalizeText(sourceRow.sheetName);
    const scope = scopeByName.get(normalizeLookup(sheetName));
    const row = {
      ...sourceRow,
      sheetName,
      sourceImportCode,
      importCode: sourceImportCode,
      itemName: normalizeText(sourceRow.itemName),
      status: normalizeText(sourceRow.status),
      rejectReason: normalizeText(sourceRow.rejectReason),
      notes: normalizeText(sourceRow.notes),
    };

    if (!scope) return;

    if (!row.importCode) {
      addError(errors, row, "importCode", sourceRow.importCode, "STT/Mã dự toán là bắt buộc");
      return;
    }

    if (!row.itemName) {
      addError(errors, row, "itemName", sourceRow.itemName, "Dự toán là bắt buộc");
      return;
    }

    const scopeId = getScopeId(scope);
    const difficultyLevel = parseNumberValue(sourceRow.difficultyLevel);
    const matchingItems = itemRows.filter(
      (candidate) =>
        String(getScopeId(candidate)) === String(scopeId) &&
        normalizeImportCode(getItemCode(candidate)) === row.importCode,
    );
    const item = difficultyLevel === null
      ? (matchingItems.length === 1 ? matchingItems[0] : null)
      : matchingItems.find((candidate) => Number(getDifficultyLevel(candidate)) === difficultyLevel);

    if (!item) {
      const duplicateCodeHint = matchingItems.length > 1 && difficultyLevel === null
        ? " File có nhiều dòng cùng mã; vui lòng bổ sung cột Khó khăn để phân biệt."
        : "";
      addError(
        errors,
        row,
        "importCode",
        sourceRow.importCode,
        `Dự toán "${sourceRow.importCode}" không tồn tại hoặc không khớp mức khó khăn trong sheet/phân cấp "${sheetName}".${duplicateCodeHint}`,
      );
      return;
    }

    if (itemHasChildren(item)) {
      addError(errors, row, "importCode", sourceRow.importCode, "Dự toán cha không được import nghiệm thu trực tiếp");
    }

    if (normalizeLookup(getItemName(item)) !== normalizeLookup(row.itemName)) {
      addError(
        errors,
        row,
        "itemName",
        sourceRow.itemName,
        `Tên dự toán không khớp với dữ liệu đã lưu: "${getItemName(item)}"`,
      );
    }

    const itemKey = String(getItemId(item));
    if (fileItemKeys.has(itemKey)) {
      addError(errors, row, "importCode", sourceRow.importCode, "Dự toán bị trùng trong file import");
    }
    fileItemKeys.add(itemKey);

    if (currentAcceptanceItemIds.has(itemKey)) {
      addError(errors, row, "importCode", sourceRow.importCode, "Dự toán đã có chi tiết trong kỳ nghiệm thu hiện tại");
    }

    const subcontractQuantity = parseNumberValue(item.subcontractQuantity ?? item.SubcontractQuantity);
    const unitPrice = parseNumberValue(item.unitPrice ?? item.UnitPrice);
    if (Number.isNaN(subcontractQuantity) || subcontractQuantity === null) {
      addError(errors, row, "", "", "Dự toán đã lưu chưa có khối lượng giao khoán hợp lệ");
    }
    if (Number.isNaN(unitPrice) || unitPrice === null) {
      addError(errors, row, "", "", "Dự toán đã lưu chưa có đơn giá hợp lệ");
    }

    const reportedQuantity = parseNumberValue(sourceRow.reportedQuantity);
    if (reportedQuantity === null) {
      addError(errors, row, "reportedQuantity", sourceRow.reportedQuantity, "Khối lượng thực hiện là bắt buộc");
      return;
    }
    if (Number.isNaN(reportedQuantity) || reportedQuantity < 0) {
      addError(errors, row, "reportedQuantity", sourceRow.reportedQuantity, "Khối lượng thực hiện phải là số hợp lệ và không âm");
      return;
    }

    const acceptedBefore = acceptedBeforeByItemId.get(itemKey) || 0;
    const remainingBefore = Math.max((subcontractQuantity || 0) - acceptedBefore, 0);
    if (isGreaterThan(reportedQuantity, remainingBefore)) {
      addError(
        errors,
        row,
        "reportedQuantity",
        sourceRow.reportedQuantity,
        `Khối lượng thực hiện không được lớn hơn khối lượng còn lại (${remainingBefore})`,
      );
      return;
    }

    const acceptedQuantity = parseNumberValue(sourceRow.acceptedQuantity);
    if (acceptedQuantity === null) {
      addError(errors, row, "acceptedQuantity", sourceRow.acceptedQuantity, "Khối lượng nghiệm thu là bắt buộc");
      return;
    }
    if (Number.isNaN(acceptedQuantity) || acceptedQuantity < 0) {
      addError(errors, row, "acceptedQuantity", sourceRow.acceptedQuantity, "Khối lượng nghiệm thu phải là số hợp lệ và không âm");
      return;
    }
    if (isGreaterThan(acceptedQuantity, reportedQuantity)) {
      addError(errors, row, "acceptedQuantity", sourceRow.acceptedQuantity, "Khối lượng nghiệm thu không được lớn hơn khối lượng thực hiện");
      return;
    }
    if (isGreaterThan(acceptedQuantity, remainingBefore)) {
      addError(
        errors,
        row,
        "acceptedQuantity",
        sourceRow.acceptedQuantity,
        `Khối lượng nghiệm thu không được lớn hơn khối lượng còn lại (${remainingBefore})`,
      );
      return;
    }

    if (!row.status) {
      addError(errors, row, "status", sourceRow.status, "Trạng thái nghiệm thu là bắt buộc");
      return;
    }
    if (!statusMap.has(normalizeLookup(row.status))) {
      addError(
        errors,
        row,
        "status",
        sourceRow.status,
        `"${row.status}" không nằm trong danh sách trạng thái nghiệm thu hợp lệ`,
      );
      return;
    }

    normalizedRows.push({
      row,
      item,
      scope,
      subcontractQuantity,
      unitPrice,
      reportedQuantity,
      acceptedQuantity,
      remainingBefore,
      status: statusMap.get(normalizeLookup(row.status)),
      rejectReason: row.rejectReason || null,
      notes: row.notes || null,
    });
  });

  const validRows = normalizedRows.map((entry) => {
    const calculated = calculateAcceptanceValues(entry);
    return {
      acceptanceProfileId: acceptanceProfileId ? Number(acceptanceProfileId) : undefined,
      itemId: Number(getItemId(entry.item)),
      itemCode: getItemCode(entry.item),
      itemName: getItemName(entry.item),
      unit: entry.item.unit ?? entry.item.Unit ?? null,
      subcontractItemScopeId: getScopeId(entry.scope),
      scopeName: getScopeName(entry.scope),
      subcontractQuantity: entry.subcontractQuantity,
      reportedQuantity: entry.reportedQuantity,
      acceptedQuantity: entry.acceptedQuantity,
      rejectedQuantity: calculated.rejectedQuantity,
      remainingQuantity: calculated.remainingQuantity,
      unitPrice: entry.unitPrice,
      acceptanceAmount: calculated.acceptanceAmount,
      status: entry.status,
      rejectReason: entry.rejectReason,
      notes: entry.notes,
    };
  });

  return {
    isValid: errors.length === 0 && validRows.length > 0,
    errors,
    items: errors.length === 0 ? validRows : [],
  };
};

export default validateAcceptanceItemsImport;
